import { useState, useEffect, useRef } from 'react';
import { Users, Eye, EyeOff, RotateCcw, Crown, Copy } from 'lucide-react';
import { Player, WebSocketMessage, GameState, CompletedTicket } from '../types';

const PlanningPoker = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentUser, setCurrentUser] = useState<Player | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [currentTicket, setCurrentTicket] = useState('');
  const [votesRevealed, setVotesRevealed] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [completedTickets, setCompletedTickets] = useState<CompletedTicket[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [copiedSessionId, setCopiedSessionId] = useState(false);
  const [ticketInput, setTicketInput] = useState('');
  const [manualEstimate, setManualEstimate] = useState('');
  const [showSizingGuide, setShowSizingGuide] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playerIdRef = useRef<string | null>(null);

  const fibonacciSizes = ['0', '1', '2', '3', '5', '8', '13', '21', '?'];

  // Sizing notes data
  const sizingNotes = [
    { size: "0", time: "10 minutes", complexity: "None - routine task with known steps and guaranteed outcome", example: "Updating a button color in CSS, fixing a typo in content, sending a status update email" },
    { size: "1", time: "1 hour", complexity: "Very low - familiar task with minimal variables", example: "Creating a simple WordPress widget, updating Drupal documentation, preparing a sprint report" },
    { size: "2", time: "1 to 2 hours", complexity: "Low - well-understood task with some minor variables", example: "Implementing a WordPress shortcode, creating wireframes for a Drupal block" },
    { size: "3", time: "2 - 6 hours", complexity: "Moderate - familiar patterns with some unknown aspects", example: "Building a custom WordPress widget with state management, implementing a Drupal hook, designing a custom content type" },
    { size: "5", time: "6 - 12 hours", complexity: "Medium - combination of known and unknown challenges, potential external dependencies", example: "Integrating a third-party API with WordPress, implementing complex WooCommerce logic, creating prototypes for a small website" },
    { size: "8", time: "8 - 16 hours", complexity: "Medium-high - significant unknowns, multiple integrated components", example: "Migrating to a new Drupal version, implementing a complex WordPress plugin with multiple dependencies" },
    { size: "13", time: "16 - 40 hours", complexity: "High - considerable research required, multiple unknowns, risk of scope changes", example: "Architecting a new Drupal module, designing a major WordPress feature from scratch, implementing a custom WordPress taxonomy system" },
    { size: "21", time: "40 hours +", complexity: "Very high - unexplored territory, significant research needed, high risk of pivots", example: "Building an entirely new WordPress plugin ecosystem, implementing a complex Drupal/WordPress integration, developing a custom Drupal framework" }
  ];

  function SizingGuide({ notes, open }: { notes: typeof sizingNotes, open: boolean }) {
    if (!open) return null;
    return (
      <div className="mt-6 p-4 bg-white text-gray-900 border border-gray-300 rounded shadow-lg">
        <h2 className="text-lg font-bold mb-2">Sizing Guide</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left">Size</th>
                <th className="px-2 py-1 text-left">Time</th>
                <th className="px-2 py-1 text-left">Complexity & Uncertainty</th>
                <th className="px-2 py-1 text-left">Example</th>
              </tr>
            </thead>
            <tbody>
              {notes.map(note => (
                <tr key={note.size} className="even:bg-gray-100 text-left">
                  <td className="px-2 py-1 font-bold">{note.size}</td>
                  <td className="px-2 py-1">{note.time}</td>
                  <td className="px-2 py-1">{note.complexity}</td>
                  <td className="px-2 py-1">{note.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // WebSocket connection management
  const connectWebSocket = () => {
    if (wsRef.current) {
      console.log('WebSocket connection already exists or is in progress.');
      return;
    }
    
    // In dev mode, connect to the local worker. In prod, connect to the deployed Worker backend.
    const isDev = import.meta.env.DEV;
    const wsUrl = isDev
      ? `ws://localhost:8787/ws/${sessionId}`
      : `wss://poker-planning-backend.storiesandscience.co.za/ws/${sessionId}`;
    
    console.log(`Attempting to connect to WebSocket at: ${wsUrl}`);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionError('');
        console.log('WebSocket connected successfully.');
        
        // Now that we are connected, send the join message
        sendWebSocketMessage({
            type: 'join',
            sessionId: sessionId,
            data: {
              playerName: displayName,
            }
        });
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('[WS] Received message:', message);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null; // Clear the ref on close
        console.log('WebSocket disconnected. Attempting to reconnect...');
        // Attempt to reconnect after 3 seconds
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setConnectionError('Connection failed. Check the browser console for details.');
        // The onclose event will fire next, triggering reconnect logic.
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionError('Failed to create WebSocket. Check the URL and server status.');
    }
  };

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case 'welcome': {
        const { playerId, gameState } = message.data;
        console.log(`Received welcome message. My new ID is: ${playerId}`);
        playerIdRef.current = playerId;
        // Fallthrough to update the rest of the game state
        message = { ...message, type: 'game_state', data: gameState };
        break;
      }
      // eslint-disable-next-line no-fallthrough
      case 'game_state': {
        const state: GameState = message.data;
        setPlayers(state.players);
        setCurrentTicket(state.currentTicket);
        setVotesRevealed(state.votesRevealed);
        setCompletedTickets(state.completedTickets);
        console.log('[WS] Updated players:', state.players);
        break;
      }
      case 'player_list':
        setPlayers(message.data.players);
        break;
        
      default:
        console.log('Unhandled message type:', message.type);
    }
  };

  useEffect(() => {
    if (playerIdRef.current) {
      const me = players.find(p => p.id === playerIdRef.current);
      if (me) {
        setCurrentUser(me);
        console.log('[WS] useEffect set currentUser:', me);
      } else {
        // This can happen briefly when a player disconnects.
        console.warn('[WS] useEffect: My player ID not found in player list:', playerIdRef.current, players);
      }
    }
    // Log the full state after every update
    console.log('[WS] useEffect state:', {
      currentUser,
      players,
      currentTicket,
    });
  }, [players, currentTicket]);

  useEffect(() => {
    // When the server sends a new ticket name, update the host's input field as well
    if (currentUser?.isHost) {
      setTicketInput(currentTicket);
    }
  }, [currentTicket, currentUser?.isHost]);

  const sendWebSocketMessage = (message: Partial<WebSocketMessage>) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('Attempted to send message, but WebSocket is not open.');
    }
  };

  useEffect(() => {
    if (gameStarted && !wsRef.current) {
      // We need a display name to join
      if (displayName) {
        connectWebSocket();
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [gameStarted, displayName]);

  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const createSession = () => {
    if (!displayName.trim()) {
      alert("Please enter a display name first.");
      return;
    }
    
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    setDisplayName(displayName.trim());
    setGameStarted(true); // This triggers the useEffect to connect
  };

  const joinSession = () => {
    if (!sessionId.trim() || !displayName.trim()) {
      alert("Please enter a display name and session ID.");
      return;
    }
    
    setDisplayName(displayName.trim());
    setGameStarted(true); // This triggers the useEffect to connect
  };

  const castVote = (size: string) => {
    if (currentUser) {
      console.log('[UI] castVote called with:', size, 'for user:', currentUser);
      sendWebSocketMessage({
        type: 'vote',
        sessionId: sessionId,
        playerId: currentUser.id,
        data: { vote: size }
      });
    }
  };

  const revealVotes = () => {
    if (currentUser?.isHost) {
      sendWebSocketMessage({
        type: 'reveal',
        sessionId: sessionId,
        data: {}
      });
    }
  };

  const startNewRound = () => {
    if (currentUser?.isHost) {
      sendWebSocketMessage({
        type: 'new_round',
        data: { manualEstimate: manualEstimate || null } // Send manual estimate if provided
      });
      setManualEstimate(''); // Clear the manual estimate input
    }
  };

  const updateTicket = (ticket: string) => {
    if (currentUser?.isHost) {
      sendWebSocketMessage({
        type: 'update_ticket',
        sessionId: sessionId,
        data: { ticket }
      });
    }
  };

  const copySessionId = async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopiedSessionId(true);
      setTimeout(() => setCopiedSessionId(false), 2000);
    } catch (error) {
      console.error('Failed to copy session ID:', error);
    }
  };

  const copyResultsList = () => {
    const list = completedTickets.map(item => 
      `${item.ticket}: ${item.estimate}`
    ).join('\n');
    
    navigator.clipboard.writeText(list).then(() => {
      alert('Results copied to clipboard!');
    }).catch(() => {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = list;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Results copied to clipboard!');
    });
  };

  const allPlayersVoted = players.length > 0 && players.every(player => player.vote !== null);
  const isHost = currentUser && currentUser.isHost;

  // Only the host can update the ticket
  const submitTicket = () => {
    if (currentUser?.isHost && ticketInput.trim()) {
      updateTicket(ticketInput.trim());
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-white/20">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Planning Poker</h1>
            <p className="text-blue-200">Multiplayer estimation with real-time collaboration</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Enter your name"
                maxLength={20}
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={createSession}
                disabled={!displayName.trim()}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
              >
                Create New Session
              </button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-blue-200">or join existing</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Session ID
                </label>
                <input
                  type="text"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase"
                  placeholder="Enter session ID"
                  maxLength={6}
                />
              </div>
              
              <button
                onClick={joinSession}
                disabled={!displayName.trim() || !sessionId.trim()}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
              >
                Join Session
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Planning Poker</h1>
              <div className="flex items-center gap-4 text-blue-200">
                <div className="flex items-center gap-2">
                  <Users size={20} />
                  <span>{players.length} player{players.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm">{isConnected ? 'Connected' : 'Disconnected'}</span>
                </div>
              </div>
              
              {connectionError && (
                <div className="mt-2 text-red-300 text-sm bg-red-500/20 px-3 py-2 rounded-lg">
                  {connectionError}
                </div>
              )}
              
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-blue-200">Session ID:</span>
                <code className="bg-white/20 px-2 py-1 rounded text-white font-mono text-sm">{sessionId}</code>
                <button
                  onClick={copySessionId}
                  className="text-blue-300 hover:text-blue-200 transition-colors"
                >
                  <Copy size={16} />
                </button>
                {copiedSessionId && (
                  <span className="text-green-300 text-sm">Copied!</span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {isHost && (
                <>
                  <button
                    onClick={revealVotes}
                    disabled={!allPlayersVoted || votesRevealed}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors"
                  >
                    <Eye size={18} />
                    Reveal Votes
                  </button>
                  
                  <button
                    onClick={startNewRound}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    <RotateCcw size={18} />
                    New Round
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Current Ticket */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-blue-200 mb-2">
              Current Ticket/Story
            </label>
            {isHost ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={ticketInput}
                  onChange={e => setTicketInput(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter ticket description..."
                />
                <button
                  onClick={submitTicket}
                  disabled={!ticketInput.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            ) : (
              <div className="text-white text-lg min-h-[2.5rem] flex items-center">
                {currentTicket || <span className="text-blue-300">No ticket set</span>}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Voting Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6">Cast Your Vote</h2>
              
              {!votesRevealed ? (
                <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
                  {fibonacciSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => castVote(size)}
                      className={`aspect-square rounded-xl font-bold text-lg transition-all duration-200 ${
                        currentUser?.vote === size
                          ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white scale-105 shadow-lg'
                          : 'bg-white/20 text-white hover:bg-white/30 hover:scale-105'
                      }`}
                      disabled={!currentUser || votesRevealed}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <EyeOff size={48} className="mx-auto text-blue-300 mb-4" />
                  <p className="text-xl text-white">Votes are revealed! Check the results below.</p>
                </div>
              )}
              
              {currentUser?.vote && !votesRevealed && (
                <div className="mt-6 text-center">
                  <p className="text-blue-200 mb-2">Your vote:</p>
                  <div className="inline-block bg-gradient-to-br from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-bold text-xl">
                    {currentUser.vote}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Players Panel */}
          <div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6">Players</h2>
              
              <div className="space-y-3">
                {players.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between bg-white/10 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-2">
                      {player.isHost && <Crown size={16} className="text-yellow-400" />}
                      <span className="text-white font-medium">{player.name}</span>
                      {player.id === currentUser?.id && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">You</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {votesRevealed ? (
                        <span className="bg-gradient-to-br from-blue-500 to-purple-600 text-white px-3 py-1 rounded-lg font-bold">
                          {player.vote || '?'}
                        </span>
                      ) : (
                        <div className={`w-8 h-8 rounded-full ${
                          player.vote ? 'bg-green-500' : 'bg-gray-400'
                        }`} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {allPlayersVoted && !votesRevealed && (
                <div className="mt-4 text-center">
                  <p className="text-green-300 font-semibold">All players have voted!</p>
                  {isHost && (
                    <p className="text-blue-200 text-sm mt-1">Click "Reveal Votes" to see results</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results */}
        {votesRevealed && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mt-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6">Results</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4">
              {fibonacciSizes.map((size) => {
                const count = players.filter(player => player.vote === size).length;
                const percentage = players.length > 0 ? (count / players.length) * 100 : 0;
                
                return (
                  <div key={size} className="text-center">
                    <div className="bg-white/20 rounded-lg p-4 mb-2">
                      <div className="text-2xl font-bold text-white mb-2">{size}</div>
                      <div className="text-3xl font-bold text-blue-300">{count}</div>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {isHost && (
              <div className="mt-6 pt-6 border-t border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">Override Final Estimate</h3>
                <p className="text-blue-200 mb-3 text-sm max-w-md mx-auto">If consensus isn't reached, you can manually set the estimate before starting the next round.</p>
                <div className="flex gap-2 items-center max-w-xs mx-auto">
                  <input
                    type="text"
                    value={manualEstimate}
                    onChange={(e) => setManualEstimate(e.target.value)}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="e.g., 8"
                  />
                  <p className="text-blue-200">points</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Completed Tickets */}
        {completedTickets.length > 0 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mt-6 border border-white/20">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Completed Tickets</h2>
              <button
                onClick={copyResultsList}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Copy List
              </button>
            </div>
            
            <div className="space-y-3">
              {completedTickets.slice().reverse().map((item, index) => (
                <div key={index} className="bg-white/10 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium">{item.ticket}</p>
                    <p className="text-blue-200 text-sm">{item.timestamp}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg font-bold text-lg">
                    {item.estimate}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            className="px-4 py-2 bg-blue-700 text-white rounded shadow hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            onClick={() => setShowSizingGuide(s => !s)}
            aria-expanded={showSizingGuide}
            aria-controls="sizing-guide-section"
          >
            {showSizingGuide ? "Hide Sizing Guide" : "Show Sizing Guide"}
          </button>
          <div id="sizing-guide-section">
            <SizingGuide notes={sizingNotes} open={showSizingGuide} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningPoker; 